import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { api } from "../lib/api";
import { ListPageShell } from "../components/shell/ListPageShell";
import { DataTable, type Column } from "../components/DataTable";
import { FLEXXI_UPFRONT_MODES, FLEXXI_UPFRONT_MODE_LABELS, type FlexxiUpfrontMode } from "@gaole/shared";
import { Button, Input, Badge, Select } from "../components/ui";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { rm } from "../lib/format";

interface TemplateRow {
  id: string;
  code: string;
  name: string;
  active: boolean;
  termMonths: number;
  interestRateBps: number;
  upfrontMode: FlexxiUpfrontMode;
  depositPercentBps: number;
  flexxiFeePercentBps: number;
  graceDays: number;
  lateFeeFixed: number;
  lateFeePercentBps: number;
  defaultAfterDays: number;
  sortOrder: number;
}

function bpsToPercent(bps: number): number {
  return bps / 100;
}

function percentToBps(percent: number): number {
  return Math.round(percent * 100);
}

type TemplateForm = {
  code: string;
  name: string;
  termMonths: number;
  interestPercent: number;
  upfrontMode: FlexxiUpfrontMode;
  depositPercent: number;
  flexxiFeePercent: number;
  graceDays: number;
  lateFeeFixed: number;
  lateFeePercent: number;
  defaultAfterDays: number;
  active: boolean;
  sortOrder: number;
};

function rowToForm(r: TemplateRow): TemplateForm {
  return {
    code: r.code,
    name: r.name,
    termMonths: r.termMonths,
    interestPercent: bpsToPercent(r.interestRateBps),
    upfrontMode: r.upfrontMode,
    depositPercent: bpsToPercent(r.depositPercentBps),
    flexxiFeePercent: bpsToPercent(r.flexxiFeePercentBps),
    graceDays: r.graceDays,
    lateFeeFixed: r.lateFeeFixed,
    lateFeePercent: bpsToPercent(r.lateFeePercentBps),
    defaultAfterDays: r.defaultAfterDays,
    active: r.active,
    sortOrder: r.sortOrder,
  };
}

const emptyForm = (sortOrder: number): TemplateForm => ({
  code: "",
  name: "",
  termMonths: 3,
  interestPercent: 8,
  upfrontMode: "FIRST_INSTALLMENT",
  depositPercent: 20,
  flexxiFeePercent: 3,
  graceDays: 3,
  lateFeeFixed: 10,
  lateFeePercent: 0,
  defaultAfterDays: 30,
  active: true,
  sortOrder,
});

function formToApiBody(form: TemplateForm) {
  return {
    code: form.code,
    name: form.name,
    termMonths: form.termMonths,
    active: form.active,
    sortOrder: form.sortOrder,
    interestRateBps: percentToBps(form.interestPercent),
    upfrontMode: form.upfrontMode,
    depositPercentBps: percentToBps(form.depositPercent),
    flexxiFeePercentBps: percentToBps(form.flexxiFeePercent),
    graceDays: form.graceDays,
    lateFeeFixed: form.lateFeeFixed,
    lateFeePercentBps: percentToBps(form.lateFeePercent),
    defaultAfterDays: form.defaultAfterDays,
  };
}

function formatLatePenalty(r: TemplateRow): string {
  const parts: string[] = [];
  if (r.lateFeeFixed > 0) parts.push(rm(r.lateFeeFixed));
  if (r.lateFeePercentBps > 0) parts.push(`${bpsToPercent(r.lateFeePercentBps)}% of overdue`);
  if (!parts.length) return "None";
  return `${parts.join(" + ")} after ${r.graceDays}d grace`;
}

export function FlexxiTemplates() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canWrite = can("flexxi.manage_templates");
  const [editId, setEditId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<TemplateForm>(emptyForm(0));

  const q = useQuery({
    queryKey: ["flexxi", "templates"],
    queryFn: () => api<{ templates: TemplateRow[] }>("/admin/flexxi/templates"),
  });

  const saveMut = useMutation({
    mutationFn: () => {
      const body = formToApiBody(form);
      if (editId) return api(`/admin/flexxi/templates/${editId}`, { method: "PATCH", body });
      return api("/admin/flexxi/templates", { method: "POST", body });
    },
    onSuccess: () => {
      toast("success", "Saved");
      void qc.invalidateQueries({ queryKey: ["flexxi", "templates"] });
      setEditId(null);
      setCreating(false);
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const columns: Column<TemplateRow>[] = [
    { key: "code", header: "Code", render: (r) => r.code, sortValue: (r) => r.code },
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "term", header: "Term", render: (r) => `${r.termMonths} mo` },
    {
      key: "rates",
      header: "Rates",
      render: (r) =>
        `${bpsToPercent(r.interestRateBps)}% p.a. · ${
          r.upfrontMode === "FIRST_INSTALLMENT" ? "1st instalment upfront" : `${bpsToPercent(r.depositPercentBps)}% dep`
        } · ${bpsToPercent(r.flexxiFeePercentBps)}% fee`,
      hideSm: true,
    },
    {
      key: "late",
      header: "Late penalty",
      render: (r) => formatLatePenalty(r),
      hideSm: true,
    },
    {
      key: "active",
      header: "Status",
      render: (r) => <Badge color={r.active ? "green" : "gray"}>{r.active ? "Active" : "Off"}</Badge>,
    },
  ];

  const panelOpen = Boolean(creating || editId);
  const rows = q.data?.templates ?? [];

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={q.isLoading}
        onRowClick={
          canWrite
            ? (r) => {
                setCreating(false);
                setEditId(r.id);
                setForm(rowToForm(r));
              }
            : undefined
        }
        toolbar={{
          trailing: canWrite ? (
            <Button
              size="sm"
              onClick={() => {
                setCreating(true);
                setEditId(null);
                setForm(emptyForm(rows.length));
              }}
            >
              Create
            </Button>
          ) : undefined,
          onRefresh: () => void q.refetch(),
        }}
        empty={{ icon: CalendarClock, title: "No plan templates", hint: "Create 3M and 12M templates for checkout." }}
      />

      <RightSidebarPanel
        open={panelOpen}
        title={editId ? "Edit template" : "Create template"}
        onClose={() => {
          setEditId(null);
          setCreating(false);
        }}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditId(null);
                setCreating(false);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
              Save
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Input label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
          <Input label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input
            label="Term (months)"
            type="number"
            value={String(form.termMonths)}
            onChange={(e) => setForm((f) => ({ ...f, termMonths: Number(e.target.value) }))}
          />
          <Input
            label="Interest (% per year)"
            type="number"
            step="0.01"
            hint="8 = 8% annual rate on financed amount"
            value={String(form.interestPercent)}
            onChange={(e) => setForm((f) => ({ ...f, interestPercent: Number(e.target.value) }))}
          />
          <Select
            label="Paid at checkout"
            hint="First instalment: no deposit, month 1 is collected at order and the rest monthly"
            value={form.upfrontMode}
            onChange={(e) => setForm((f) => ({ ...f, upfrontMode: e.target.value as FlexxiUpfrontMode }))}
          >
            {FLEXXI_UPFRONT_MODES.map((m) => (
              <option key={m} value={m}>
                {FLEXXI_UPFRONT_MODE_LABELS[m]}
              </option>
            ))}
          </Select>
          {form.upfrontMode === "DEPOSIT" ? (
            <Input
              label="Deposit (% of price)"
              type="number"
              step="0.01"
              hint="20 = pay 20% upfront as deposit"
              value={String(form.depositPercent)}
              onChange={(e) => setForm((f) => ({ ...f, depositPercent: Number(e.target.value) }))}
            />
          ) : null}
          <Input
            label="Flexxi fee (% of price)"
            type="number"
            step="0.01"
            hint="3 = 3% platform fee (due at checkout)"
            value={String(form.flexxiFeePercent)}
            onChange={(e) => setForm((f) => ({ ...f, flexxiFeePercent: Number(e.target.value) }))}
          />
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Late payment penalty</p>
          <Input
            label="Grace period (days)"
            type="number"
            min={0}
            hint="No late charge until this many days after the instalment due date"
            value={String(form.graceDays)}
            onChange={(e) => setForm((f) => ({ ...f, graceDays: Number(e.target.value) }))}
          />
          <Input
            label="Fixed late charge (MYR)"
            type="number"
            min={0}
            step="0.01"
            hint="Flat penalty applied once per overdue instalment after grace (e.g. 10)"
            value={String(form.lateFeeFixed)}
            onChange={(e) => setForm((f) => ({ ...f, lateFeeFixed: Number(e.target.value) }))}
          />
          <Input
            label="Late charge (% of overdue instalment)"
            type="number"
            min={0}
            step="0.01"
            hint="Optional percentage of the unpaid instalment amount; combined with fixed charge"
            value={String(form.lateFeePercent)}
            onChange={(e) => setForm((f) => ({ ...f, lateFeePercent: Number(e.target.value) }))}
          />
          <Input
            label="Default after (days past grace)"
            type="number"
            min={1}
            hint="Days after grace ends before the plan can be defaulted"
            value={String(form.defaultAfterDays)}
            onChange={(e) => setForm((f) => ({ ...f, defaultAfterDays: Number(e.target.value) }))}
          />
        </div>
      </RightSidebarPanel>
    </ListPageShell>
  );
}
